import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createPageSpeedMeasurement } from "../openapi/api-client.ts";
import { TriggerMeasurementError } from "../errors/TriggerMeasurementError.ts";
import type { PageSpeedMeasurement } from "./useLastMeasurement.ts";

interface RateLimitBody {
  message?: string;
  retryAfterSeconds?: number;
}

export const useTriggerMeasurement = (
  projectId: string,
  appInstallationId: string,
) => {
  const queryClient = useQueryClient();

  return useMutation<PageSpeedMeasurement, TriggerMeasurementError>({
    mutationFn: async () => {
      const response = await createPageSpeedMeasurement({
        path: { projectId, appInstallationId },
      });

      if (response.data) {
        return response.data;
      }

      const status: number = response.response.status;
      const fallbackMessage = response.error.message;

      if (status === 429) {
        const body = response.error as RateLimitBody | undefined;
        throw new TriggerMeasurementError(
          "rate-limit",
          body?.message ?? "rate limit reached",
          body?.retryAfterSeconds,
        );
      }
      if (status === 422) {
        throw new TriggerMeasurementError("no-host", fallbackMessage);
      }
      throw new TriggerMeasurementError("unknown", fallbackMessage);
    },
    onSuccess: (pendingMeasurement) => {
      // Seed the cache with the pending doc so useLastMeasurement picks it up
      // immediately and starts polling via refetchInterval.
      queryClient.setQueryData<PageSpeedMeasurement | null>(
        ["pageSpeedMeasurement", { projectId, appInstallationId }],
        pendingMeasurement,
      );
    },
  });
};
